// @ts-nocheck
// Theme JS runs without a tsconfig; type annotations would require build tooling
// not available in this Shopify context.
/**
 * Fulfillment Details Drawer — global, page-agnostic ES module.
 *
 * A right-side drawer that collects the fulfillment details the store selector
 * can't: the pickup/delivery time, who the order is for (first name, last name,
 * phone, and email for a delivery), and the instructions. Everything except the
 * instruction is required.
 *
 * TIME SLOTS — come from the EA availability API, which is the source of truth:
 * one request per store + date + cart returns `pickupTimeSlots` and
 * `deliveryTimeSlots` already clamped to the store's schedule, prep margin and
 * same-day cutoffs. Nothing here generates slot values. The one exception is the
 * catering tomorrow +1h/+4h rule, opted into with
 * `{ bookingRules: BOOKING_RULES.catering }` — and it only ever drops leading
 * slots, never invents one. See applyCateringTomorrowRule.
 *
 * This drawer does NOT make that request. The host makes it as part of deciding
 * whether to open at all — a date with no bookable times is a message on the cart,
 * not a drawer with an empty dropdown — and hands the answer over as
 * `{ availability }`. Both hosts call requestAvailability() below, so there is
 * still exactly one implementation of the request; what moved is only WHO calls
 * it. See #runCheckoutValidation in assets/ea-cart-drawer.js and
 * openCheckoutDrawer in assets/catering-cart.js.
 *
 * MOUNTING — once per page, from `layout/theme.liquid`:
 *   {% render 'fulfillment-details-drawer' %}
 * The snippet loads this module itself. Mount it exactly once: the drawer is a
 * singleton and a second copy of the markup would be ignored.
 *
 * OPENING — from any page, either way:
 *   • markup:  <button data-open-fulfillment-details>Review &amp; Checkout</button>
 *   • script:  window.openFulfillmentDetails()
 *              document.dispatchEvent(new CustomEvent('fulfillment-details:open'))
 * Both accept an optional redirect override — `data-fulfillment-redirect="/cart"`
 * on the trigger, or `window.openFulfillmentDetails({ redirectTo: '/cart' })`.
 *
 * BACK NAVIGATION — a host that opens this drawer from another one passes
 * `onBack`, which reveals a back arrow in the header and runs after this drawer
 * closes. The cart drawer uses it to reopen itself (see #handleCheckoutClick in
 * assets/ea-cart-drawer.js); the catering sticky bar passes nothing, so no arrow
 * appears there — there's nothing behind it to return to.
 *
 * COMPLETING — clicking the drawer's submit button:
 *   1. writes the collected details to cart attributes via POST /cart/update.js
 *   2. redirects to the checkout (or `redirectTo`)
 * Between the two it dispatches a **cancelable** `fulfillment-details:saved` on
 * document, with `detail = { values, attributes, isPickup, storeSelection }`.
 * A host that owns its own checkout (e.g. the catering cart, which builds a
 * separate Storefront API cart) calls `event.preventDefault()` to take over —
 * the drawer then skips both the cart write and the redirect, and the host uses
 * `detail.attributes` so the order carries byte-identical attribute keys.
 *
 * CONTEXT — the store, zip, date, fulfillment type and store hours all come from
 * the shopper's saved store selection (`ea-store-availability`, written by
 * `assets/store-availability.js` from the header store selector). Without a
 * selection there are no time slots to offer, so opening the drawer opens the
 * store selector first and continues once a store is chosen.
 *
 * STORAGE — this module stores NOTHING of its own. The details it collects live
 * only in the fields until submit, and closing the drawer discards them; the cart
 * attributes it writes are the single record. Don't add a localStorage cache here:
 * the recipient name, phone and email are personal data, and a stored copy would
 * outlive the order it belonged to.
 */

import { AVAILABILITY_API } from '@theme/ea-api-endpoints';

// ── Configuration ─────────────────────────────────────────────────────────────

/** Store selection written by the header store selector. */
const STORE_SELECTION_KEY = 'ea-store-availability';

const DEFAULT_REDIRECT_URL = '/checkout';

/**
 * How long a chosen time stays good for. Past this, submitting clears it and asks
 * for it again: for catering the bookable window moves with the clock (see
 * applyCateringTomorrowRule), so a selection made half an hour ago may no longer be
 * one the store can honour.
 */
const TIME_SELECTION_TTL_MS = 30 * 60 * 1000;
const CART_UPDATE_TIMEOUT_MS = 10000;
const CLOSE_ANIMATION_MS = 300;
const INSTRUCTION_MAX_LENGTH = 250;

/**
 * How long an open request that was waiting on a store selection stays live. Past
 * this, a store chosen from the header is treated as ordinary browsing rather
 * than the shopper finishing what they started, so the drawer doesn't reappear
 * out of nowhere.
 */
const PENDING_OPEN_TTL_MS = 120000;

/**
 * Which set of booking rules a given order follows.
 *
 * `storeHours` is the plain one and the default: the window is simply the store's
 * opening hours, trimmed by an hour at each end. Nothing else is applied — the
 * point is to show the shopper the store's ordering window.
 *
 * `catering` adds the prep rules on top, because a catering order has to be made
 * rather than handed over: the tomorrow cutoff, and no times that have already
 * passed today. Only the catering flow asks for these.
 */
export const BOOKING_RULES = Object.freeze({
  storeHours: 'store-hours',
  catering: 'catering',
});

/**
 * Cart attribute keys the collected details are written to.
 *
 * `fulfillment_time` and `fulfillment_additional_info` are unprefixed, matching
 * the `fulfillment_date` / `order_type` / `store_id` keys that
 * `assets/store-availability.js` writes — one `fulfillment_*` family describing
 * the order's fulfillment, whatever page it was assembled on. The instruction
 * keys keep their `_` prefix (internal-only) and their pickup/delivery split.
 *
 * Exported as the single source of truth: every host that ships these details —
 * the AJAX cart write below, or a Storefront `cartCreate` elsewhere — must use
 * these exact keys so an order reads the same however it was placed.
 */
export const FULFILLMENT_ATTR_KEYS = {
  time: 'fulfillment_time',
  additionalInfo: 'fulfillment_additional_info',
  pickupInstructions: '_pickup_instructions',
  deliveryInstructions: '_delivery_instructions',
};

/**
 * Format the recipient details into the single `fulfillment_additional_info`
 * value. Labelled and pipe-separated so it reads cleanly on the order in admin
 * and can still be parsed downstream. The email is delivery-only.
 *
 * This format IS the downstream contract — change it here, in one place, rather
 * than assembling the string at a call site.
 *
 * @param {{orderType?: string, firstName?: string, lastName?: string, phone?: string, email?: string}} values
 * @returns {string} '' when nothing has been entered
 */
export function formatAdditionalInfo(values) {
  const v = values || {};
  const pickup = String(v.orderType).toLowerCase() === 'pickup';
  const name = [v.firstName, v.lastName].map((part) => (part || '').trim()).filter(Boolean).join(' ');
  const parts = [];
  if (name) parts.push(`Name: ${name}`);
  if (v.phone) parts.push(`Phone: ${String(v.phone).trim()}`);
  if (!pickup && v.email) parts.push(`Email: ${String(v.email).trim()}`);
  return parts.join(' | ');
}

/**
 * Map the collected values onto cart attributes.
 *
 * Every key is always present — the ones that don't apply carry an empty string,
 * which is how /cart/update.js CLEARS a previously written attribute. Without
 * that, switching from pickup to delivery would leave a stale
 * `_pickup_instructions` on the cart. Hosts that pass attributes to the
 * Storefront API must drop the empties themselves — that API rejects blanks.
 *
 * @param {{orderType?: string, time?: string, firstName?: string, lastName?: string, phone?: string, email?: string, instruction?: string}} values
 * @returns {Record<string, string>}
 */
export function buildFulfillmentAttributeMap(values) {
  const v = values || {};
  const pickup = String(v.orderType).toLowerCase() === 'pickup';
  return {
    [FULFILLMENT_ATTR_KEYS.time]: v.time || '',
    [FULFILLMENT_ATTR_KEYS.additionalInfo]: formatAdditionalInfo(v),
    [FULFILLMENT_ATTR_KEYS.pickupInstructions]: pickup ? v.instruction || '' : '',
    [FULFILLMENT_ATTR_KEYS.deliveryInstructions]: pickup ? '' : v.instruction || '',
  };
}

// ── Store selection (shared context) ──────────────────────────────────────────

const EMPTY_SELECTION = {
  zipCode: '',
  date: '',
  fulfillmentType: '',
  deliveryType: '',
  storeName: '',
  storeAddress: '',
  store: {},
};

/**
 * Read the shopper's store selection fresh from localStorage each call, so a
 * store chosen after page load is picked up on the next open. Normalises the
 * stored payload; the nested `store` is tolerated as an object or a JSON string.
 *
 * Exported for the same reason as isPickupSelected: hosts that need the selection
 * to build a request (the cart drawer's pre-checkout availability check) should
 * read it through here rather than keeping a second copy of the storage key and its
 * normalisation. Note the zip is `zipCode` on both the stored payload and the
 * returned object — there is no `zip` key; reading one yields undefined.
 *
 * @returns {typeof EMPTY_SELECTION}
 */
export function getStoreSelection() {
  let state = null;
  try {
    const raw = window.localStorage.getItem(STORE_SELECTION_KEY);
    if (raw) state = JSON.parse(raw);
  } catch { state = null; }
  if (!state || typeof state !== 'object') return { ...EMPTY_SELECTION };

  let store = state.store;
  if (typeof store === 'string') {
    try { store = JSON.parse(store); } catch { store = {}; }
  }
  return {
    zipCode: state.zipCode || '',
    date: state.deliveryDate || '',
    fulfillmentType: state.fulfillmentType || '',
    // Which delivery window was chosen, as the store selector's internal enum:
    // 'one_hour' | 'same_day' | 'next_day' | 'scheduled'. Distinct from
    // `fulfillmentType` ('delivery' | 'pickup' | 'shipment'), which is the method
    // rather than the window. Read by the cart drawer to title the delivery fee.
    deliveryType: state.deliveryType || '',
    storeName: state.storeName || '',
    storeAddress: state.storeAddress || '',
    storeId: state.storeId != null ? String(state.storeId) : '',
    // Sent in the availability payload; the selector saves it so a country switch
    // can invalidate the selection.
    country: state.country || 'US',
    store: store || {},
  };
}

/** Whether a store has been selected at all — the drawer needs one for its slots. */
function hasStoreSelection() {
  const selection = getStoreSelection();
  return !!(selection.zipCode && selection.storeId);
}

/**
 * Whether the saved store selection is a pickup (rather than a delivery).
 *
 * Exported because hosts decide *whether* to collect these details at all, and
 * that can turn on the fulfillment type: the cart drawer only routes pickup
 * checkouts through this drawer. Reading it from here keeps the storage key and
 * its shape in one module.
 */
export function isPickupSelected() {
  return String(getStoreSelection().fulfillmentType).toLowerCase() === 'pickup';
}


// ── Merchant copy ─────────────────────────────────────────────────────────────
// Every string lives in the snippet's JSON config island so it stays editable in
// Liquid rather than baked into this module.

let _copy = {};

function loadCopy() {
  try {
    const el = document.querySelector('[data-fulfillment-details-config]');
    if (el) _copy = JSON.parse(el.textContent) || {};
  } catch {
    _copy = {};
  }
}

/** The configured string for `key`, or `fallback` when absent/empty. */
function t(key, fallback) {
  const value = _copy[key];
  return typeof value === 'string' && value.length ? value : fallback;
}

// ── DOM refs ──────────────────────────────────────────────────────────────────
// Everything is looked up inside the drawer root, so the module never reaches
// into unrelated page markup.

let _root = null;
let _overlay = null;
let _redirectTo = DEFAULT_REDIRECT_URL;

/**
 * The element focus should return to when the drawer closes — WCAG 2.4.3. Captured
 * as `document.activeElement` on open (the trigger the shopper used). Preserved
 * across the "open store selector first, replay after a store is chosen" detour so
 * focus lands back on the original trigger, not the selector; cleared on close.
 * @type {HTMLElement|null}
 */
let _returnFocusEl = null;

/**
 * What to run when the shopper backs out, set per open. Only hosts that have
 * somewhere to go back TO pass this — the cart drawer does, the catering sticky
 * bar doesn't — and it's what decides whether the header shows a back arrow.
 * @type {(() => void)|null}
 */
let _onBack = null;

/**
 * Which booking rules this session of the drawer follows, set per open. Defaults to
 * the plain store-hours window; only the catering flow asks for more.
 * @type {string}
 */
let _bookingRules = BOOKING_RULES.storeHours;
let _submitting = false;
// Timestamp of an open request that arrived without a store selection: the drawer
// opens as soon as the shopper finishes choosing one (within PENDING_OPEN_TTL_MS).
let _openAfterStoreSelectionAt = 0;

/**
 * The parked request's own options, snapshotted so the deferred open is the same
 * open that was asked for. Replaying with only `redirectTo` would reset `onBack`
 * and `bookingRules` to their defaults — costing the cart drawer its back arrow and
 * catering its prep rules. Reading the live `_onBack` / `_bookingRules` at replay
 * time isn't equivalent: a close in between nulls `_onBack`.
 * @type {{redirectTo?: string, onBack?: (() => void)|null, bookingRules?: string}|null}
 */
let _pendingOpenOptions = null;

// When the shopper picked the current time slot. Only a real change sets it —
// reopening the dropdown rebuilds the list but doesn't renew the choice.
let _timeSelectedAt = 0;

function q(selector) {
  return _root ? _root.querySelector(selector) : null;
}

const refs = {
  time: () => q('[data-fulfillment-time]'),
  timeField: () => q('[data-fulfillment-time-field]'),
  timeSkeleton: () => q('[data-fulfillment-time-skeleton]'),
  timeRetry: () => q('[data-fulfillment-time-retry]'),
  timeHeading: () => q('[data-fulfillment-time-heading]'),
  instruction: () => q('[data-fulfillment-instruction]'),
  instructionHeading: () => q('[data-fulfillment-instruction-heading]'),
  instructionHint: () => q('[data-fulfillment-instruction-hint]'),
  instructionCount: () => q('[data-fulfillment-instruction-count]'),
  timeError: () => q('[data-fulfillment-time-error]'),
  recipientHeading: () => q('[data-fulfillment-recipient-heading]'),
  firstName: () => q('[data-fulfillment-first-name]'),
  lastName: () => q('[data-fulfillment-last-name]'),
  phone: () => q('[data-fulfillment-phone]'),
  email: () => q('[data-fulfillment-email]'),
  emailWrap: () => q('[data-fulfillment-email-wrap]'),
  contactGrid: () => q('[data-fulfillment-contact-grid]'),
  phoneError: () => q('[data-fulfillment-phone-error]'),
  emailError: () => q('[data-fulfillment-email-error]'),
  back: () => q('[data-fulfillment-back]'),
  submit: () => q('[data-fulfillment-submit]'),
  error: () => q('[data-fulfillment-error]'),
  changeStore: () => q('[data-fulfillment-change-store]'),
};

// ── Open / close ──────────────────────────────────────────────────────────────

/**
 * Open the drawer. Without a store selection there are no time slots to offer,
 * so the store selector is opened first and the drawer follows once a store is
 * chosen (see the `store-availability:change` listener in bindEvents).
 *
 * @param {{redirectTo?: string, onBack?: () => void, bookingRules?: string,
 *   availability?: {pickup: string[], delivery: string[], schedule: any}}} [options]
 *   `redirectTo` — where to send the shopper after a successful save; defaults to
 *   the checkout.
 *   `onBack` — pass a function when the shopper came from somewhere they can
 *   return to (the cart drawer). It reveals a back arrow in the header and runs
 *   after the drawer closes. Omit it and no arrow is shown, which is the catering
 *   sticky-bar case: there's nothing behind the drawer to go back to.
 *   `availability` — the bookable times, as returned by requestAvailability. The
 *   host has them already: it asks before opening so that an empty list stops the
 *   open. Effectively required — without it the drawer can only say there are no
 *   times (see applyAvailability).
 */
/**
 * Whether the drawer markup is on this page — i.e. whether openFulfillmentDetails()
 * will actually do anything.
 *
 * Needed because the global opener is assigned at module evaluation time, below,
 * regardless of whether init() found the markup. A `typeof window.openFulfillmentDetails
 * === 'function'` test therefore says nothing about mountedness, and callers that
 * suppress a default action (a checkout anchor's navigation) before opening need to
 * know up front, not from a return value after the fact.
 *
 * Checks the same two refs openFulfillmentDetails() bails on, so the answer can't
 * drift from what opening will actually do.
 *
 * @returns {boolean}
 */
export function isFulfillmentDetailsMounted() {
  return !!(_root && _overlay);
}

export function openFulfillmentDetails(options) {
  if (!_root || !_overlay) return;
  // Remember what to return focus to on close. Only capture when nothing is held
  // yet, so the store-selector detour (which opens the selector, then replays this
  // open once a store is chosen) keeps the ORIGINAL trigger rather than the
  // selector's controls. Skip the drawer's own controls — reopening while open
  // would otherwise trap the return target inside it.
  if (!_returnFocusEl) {
    const active = document.activeElement;
    if (active && active !== document.body && !_root.contains(active)) {
      _returnFocusEl = active;
    }
  }
  _redirectTo = (options && options.redirectTo) || DEFAULT_REDIRECT_URL;
  _onBack = typeof options?.onBack === 'function' ? options.onBack : null;
  _bookingRules = options?.bookingRules === BOOKING_RULES.catering ? BOOKING_RULES.catering : BOOKING_RULES.storeHours;
  // Drop the previous open's slots before anything renders, so a different cart or
  // date can't show the last answer.
  _slots = { pickup: [], delivery: [], cateringDelivery: null };
  _storeSchedule = null;
  _slotsState = 'idle';
  setBackVisible(!!_onBack);

  // Every open starts from empty fields. Nothing is stored to restore, and doing
  // it here rather than trusting the close animation's own reset means a reopen
  // within those 300ms can't show the last shopper's details. Also clears the busy
  // flag the redirect path deliberately leaves set.
  resetForm();

  if (!hasStoreSelection()) {
    if (typeof window.openStoreAvailability === 'function') {
      _openAfterStoreSelectionAt = Date.now();
      // Snapshot taken from the resolved module state rather than raw `options`, so
      // the replay reuses the same defaulting applied above.
      _pendingOpenOptions = {
        redirectTo: _redirectTo,
        onBack: _onBack,
        bookingRules: _bookingRules,
        // Carried as it arrived, which for this branch means absent: a host with no
        // store selection had nothing to ask the availability endpoint about. The
        // replayed open therefore shows the no-times message rather than slots —
        // both hosts refuse to open without a store, so nothing reaches it in
        // practice. See the `availability` option below.
        availability: options?.availability || null,
      };
      window.openStoreAvailability();
      return;
    }
    // No selector on this page — open anyway and say why the slots are empty.
    showError(t('noStoreError', 'Choose a store to see the available times.'));
  }

  _overlay.classList.remove('tw-hidden');
  _root.removeAttribute('inert');
  _root.setAttribute('aria-hidden', 'false');
  // Make the rest of the page inert so nothing behind the drawer can hold or steal
  // keyboard focus. This is what actually contains Tab; the trap below only adds the
  // wrap-around at the ends.
  setBackgroundInert();
  document.body.style.overflow = 'hidden';

  // Match the copy to pickup vs delivery, then render the times the host already
  // resolved. No request goes out from here, so there's nothing to wait for and no
  // skeleton to show. Nothing is prefilled either: the details are never stored, so
  // every open starts from empty fields. Runs BEFORE the focus rAF below so the time
  // select's disabled state is settled when it's decided where focus lands.
  configureForSelection();
  applyAvailability(options?.availability);
  updateInstructionCount();

  // Slide in on the next frame, then move focus INTO the drawer in the same frame.
  // Deferring the focus to the rAF (rather than doing it synchronously here) is what
  // makes it land: a host that opened this drawer from a native <dialog> closes that
  // dialog first, and <dialog>.close() restores focus to its opener asynchronously —
  // focusing here in the same tick would be undone by that. Once focus is inside, the
  // document-level Tab trap keeps it there. Time select is the natural first stop but
  // is disabled until slots resolve (a disabled control can't take focus), so fall
  // back to the first reachable control.
  requestAnimationFrame(() => {
    _root.classList.remove('tw-translate-x-full');
    const time = refs.time();
    if (time && !time.disabled) time.focus();
    else getFocusable()[0]?.focus();
  });
}

/** Whether the drawer is currently on screen (it slides out via tw-translate-x-full). */
function isDrawerOpen() {
  return !!_root && !_root.classList.contains('tw-translate-x-full');
}

/**
 * The drawer's currently focusable controls, in DOM order.
 *
 * Visibility is tested with `getClientRects().length` rather than `offsetParent`.
 * `offsetParent` is null for a `position: fixed` element AND unreliable inside a
 * fixed subtree across browsers — using it here risked judging every field hidden
 * and returning an empty list, which then killed Tab (see trapTabKey). A hidden
 * branch (display:none back arrow, email on a pickup, the vestigial skeleton/retry)
 * has zero client rects; a visible field has at least one. Disabled/aria-hidden
 * controls are excluded so the trap never parks focus on the disabled time select or
 * submit button.
 *
 * @returns {HTMLElement[]}
 */
function getFocusable() {
  if (!_root) return [];
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  return Array.from(_root.querySelectorAll(selector)).filter(
    (el) => el.getClientRects().length > 0 && el.getAttribute('aria-hidden') !== 'true'
  );
}

/**
 * Elements made `inert` while the drawer is open, so they can be un-inerted on close.
 * @type {HTMLElement[]}
 */
let _inertedBackground = [];

/**
 * Make everything behind the drawer `inert` while it's open — the robust way to turn
 * a plain fixed <div> into a real modal without depending on keydown interception.
 *
 * inert removes an element and its whole subtree from the tab order AND the hit-test
 * / focus system, so (a) Tab can't reach the page behind the drawer and (b) nothing
 * back there can STEAL focus via its own focusin/prefetch handlers — the failure mode
 * where "Tab does nothing / you have to click each field" because a background script
 * keeps yanking focus out. Only the drawer and its overlay stay live.
 *
 * Walks up from the drawer inert-ing siblings at each level (the standard aria-modal
 * technique) so it works wherever the snippet is mounted. Skips an already-inert node
 * (leaves it as-is on restore) and any open native <dialog> — a modal dialog lives in
 * the top layer and is immune to ancestor inert anyway, and inert-ing the selector
 * that this drawer can open on top would freeze it.
 */
function setBackgroundInert() {
  clearBackgroundInert();
  const keep = new Set([_root, _overlay]);
  let node = _root;
  while (node && node.parentElement) {
    for (const sibling of Array.from(node.parentElement.children)) {
      if (keep.has(sibling) || sibling === node) continue;
      if (sibling.hasAttribute('inert')) continue;
      if (sibling.tagName === 'DIALOG' && sibling.hasAttribute('open')) continue;
      sibling.setAttribute('inert', '');
      _inertedBackground.push(sibling);
    }
    node = node.parentElement;
    if (node === document.body || node === document.documentElement) break;
  }
}

/** Restore interactivity to everything setBackgroundInert() disabled. */
function clearBackgroundInert() {
  for (const el of _inertedBackground) el.removeAttribute('inert');
  _inertedBackground = [];
}

/**
 * Keep Tab / Shift+Tab inside the drawer while it's open — a modal must not leak
 * focus to the page behind it (WCAG 2.1.2, 2.4.3).
 *
 * Bound on `document`, NOT the drawer root, and that distinction is the fix: this
 * drawer is a plain fixed <div>, not a native <dialog>, so the browser doesn't make
 * the background inert. If focus is ever OUTSIDE the drawer — the host's
 * <dialog>.close() restoring focus to its opener, a click on the page, a timing race
 * on open — a root-bound handler never fires and Tab just walks the background,
 * leaving the drawer unreachable. Listening on the document catches that Tab and
 * pulls focus back in (`!_root.contains(active)` → first/last).
 *
 * Bails while a native modal <dialog> is open on top (the "change store" selector):
 * that dialog owns focus containment itself, and the drawer is inert behind it, so
 * pulling focus toward it would break tabbing in the dialog.
 *
 * @param {KeyboardEvent} e
 */
function trapTabKey(e) {
  if (e.key !== 'Tab' || !isDrawerOpen()) return;
  // A native modal (e.g. the store selector) opened on top manages its own focus.
  if (document.querySelector('dialog[open]')) return;
  const focusable = getFocusable();
  // Never SWALLOW Tab when we can't find anything to focus — that would freeze the
  // keyboard entirely (the reported "have to click each field" symptom). Bail and let
  // the browser's native Tab run instead.
  if (focusable.length === 0) {
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (e.shiftKey) {
    if (active === first || !_root.contains(active)) {
      e.preventDefault();
      last.focus();
    }
  } else if (active === last || !_root.contains(active)) {
    e.preventDefault();
    first.focus();
  }
}

/** Show or hide the header's back arrow (tw-hidden ↔ tw-flex). */
function setBackVisible(visible) {
  const btn = refs.back();
  if (!btn) return;
  btn.classList.toggle('tw-hidden', !visible);
  btn.classList.toggle('tw-flex', visible);
}

/**
 * Back out of the drawer: close it and hand control to whoever opened it.
 *
 * The host's callback runs AFTER the slide-out finishes, so the two panels don't
 * overlap mid-animation — the cart drawer reappearing on top of a drawer still
 * sliding away looks like a glitch. Cleared first, so a second back click during
 * the animation can't run it twice.
 */
function goBack() {
  const onBack = _onBack;
  _onBack = null;
  closeFulfillmentDetails();
  if (onBack) setTimeout(onBack, CLOSE_ANIMATION_MS);
}

/**
 * Slide the drawer out and clear its inputs.
 * @param {{saving?: boolean}} [options] `saving: true` suppresses the dismissed
 *   notice — the save path emits `fulfillment-details:saved` instead.
 */
function closeFulfillmentDetails(options) {
  if (!_root || !_overlay) return;
  _root.classList.add('tw-translate-x-full');
  // Back out of the tab order / a11y tree straight away — nothing inside needs
  // to be reachable while it slides out.
  _root.setAttribute('inert', '');
  _root.setAttribute('aria-hidden', 'true');
  // Give the rest of the page its interactivity back before restoring focus to a
  // trigger that lives out there.
  clearBackgroundInert();
  // Return focus to whatever opened the drawer (WCAG 2.4.3). Must run AFTER inert
  // is set — inert blurs the focused field to <body>, so focusing here lands on the
  // trigger rather than being pulled back. A no-op if the trigger has since left the
  // DOM (e.g. its host drawer closed); that host then manages focus via onBack.
  const returnEl = _returnFocusEl;
  _returnFocusEl = null;
  if (returnEl && typeof returnEl.focus === 'function' && document.contains(returnEl)) {
    try { returnEl.focus(); } catch { /* element not focusable right now */ }
  }
  if (!options || !options.saving) {
    document.dispatchEvent(new CustomEvent('fulfillment-details:dismissed'));
  }
  setTimeout(() => {
    _overlay.classList.add('tw-hidden');
    // Reset off-screen, once the slide-out has finished, so the shopper never
    // sees the fields clear mid-animation. Nothing is kept — closing the drawer
    // discards what was entered.
    resetForm();
  }, CLOSE_ANIMATION_MS);
  document.body.style.overflow = '';
}

/** Clear the drawer's inputs back to empty. */
function resetForm() {
  for (const field of [refs.time(), refs.instruction(), refs.firstName(), refs.lastName(), refs.phone(), refs.email()]) {
    if (field) field.value = '';
  }

  _timeSelectedAt = 0;
  clearFieldErrors();
  showError('');
  setSubmitting(false);
  updateInstructionCount();
  updateSubmitState();
}

/**
 * Whether the chosen time has gone stale — picked more than TIME_SELECTION_TTL_MS
 * ago. Checked when Checkout is clicked, because the bookable window is derived
 * from the current time: a slot chosen half an hour earlier may since have fallen
 * outside it (or past the late-order cutoff for tomorrow).
 */
function isTimeSelectionStale() {
  if (!refs.time()?.value) return false;
  return !_timeSelectedAt || Date.now() - _timeSelectedAt > TIME_SELECTION_TTL_MS;
}

// ── Copy driven by the selection ──────────────────────────────────────────────

/**
 * Point the copy at the selected fulfillment type and show the recipient email
 * for delivery only. The type is fixed per selection, so this runs on open rather
 * than reacting to input.
 */
function configureForSelection() {
  const pickup = isPickupSelected();

  const heading = refs.timeHeading();
  if (heading) {
    heading.textContent = pickup
      ? t('pickupTimeHeading', 'Select a pickup time')
      : t('deliveryTimeHeading', 'Select a delivery time');
  }

  const time = refs.time();
  const placeholder = time?.querySelector('option[value=""]');
  if (placeholder) {
    placeholder.textContent = pickup
      ? t('pickupTimePlaceholder', 'Select Pickup Time')
      : t('deliveryTimePlaceholder', 'Select Delivery Time');
  }

  const recipientHeading = refs.recipientHeading();
  if (recipientHeading) {
    recipientHeading.textContent = pickup
      ? t('pickupRecipientHeading', 'Who is picking up this order?')
      : t('deliveryRecipientHeading', 'Who should the driver ask for?');
  }

  // The recipient email is collected for delivery only; with it hidden, the phone
  // takes the full width of the contact row.
  const emailWrap = refs.emailWrap();
  if (emailWrap) emailWrap.classList.toggle('tw-hidden', pickup);
  refs.contactGrid()?.classList.toggle('--single-column', pickup);
  // Drop any email typed before a switch to pickup, so it can't be submitted
  // from a hidden field.
  if (pickup) {
    const email = refs.email();
    if (email) email.value = '';
  }

  const instructionHeading = refs.instructionHeading();
  if (instructionHeading) {
    instructionHeading.textContent = pickup
      ? t('pickupInstructionHeading', 'Pickup instruction')
      : t('deliveryInstructionHeading', 'Delivery instruction');
  }

  const instruction = refs.instruction();
  if (instruction) {
    instruction.placeholder = pickup
      ? t('pickupInstructionPlaceholder', 'Add pickup instructions')
      : t('deliveryInstructionPlaceholder', 'Add delivery instructions');
  }

  const hint = refs.instructionHint();
  if (hint) {
    hint.textContent = pickup
      ? t('pickupInstructionHint', 'Provide details to make your pickup as convenient as possible. (Ex: pickup timeframe, accessibility requirements, etc.)')
      : t('deliveryInstructionHint', 'Provide details to help us deliver your treats to the right place. (Ex: crossroads, landmarks, info for gated communities, etc.)');
  }
}

// ── Availability API ──────────────────────────────────────────────────────────
// The EA availability endpoint is the source of truth for bookable times: it
// returns `pickupTimeSlots` and `deliveryTimeSlots` already clamped to the
// store's schedule, prep margin and same-day cutoffs. The frontend renders those
// arrays and never generates slot values of its own — the single exception is the
// catering tomorrow rule, which only ever DROPS leading slots (see
// applyCateringTomorrowRule).
//
// requestAvailability() below is the request; the HOSTS call it, before they open
// this drawer, because an empty list has to stop the open rather than land inside
// it. This module only renders what they hand back.

const AVAILABILITY_TIMEOUT_MS = 15000;

/** @type {{pickup: string[], delivery: string[], cateringDelivery: string[]|null}} */
let _slots = { pickup: [], delivery: [], cateringDelivery: null };
/** Store schedule from the open's response — drives the catering tomorrow rule. */
let _storeSchedule = null;
/** 'idle' | 'ready' — 'idle' means the host handed over nothing to render. */
let _slotsState = 'idle';
/** `${storeId}:${date}:${productIds}` -> response, so a re-ask is instant. */
const _availabilityCache = new Map();

/**
 * Whether slots have been handed over. The submit button stays disabled until
 * they have: without slots there is no valid time to submit.
 */
function slotsReady() {
  return _slotsState === 'ready';
}

/**
 * Reflect the slot state onto the time section.
 *
 * The host resolves the slots BEFORE this drawer opens, so there is no in-flight
 * or failed state left to render: the skeleton and the retry button — both of
 * which existed only to cover this module's own request — stay hidden, and the
 * field is what's on screen. Hidden explicitly rather than left to the markup's
 * defaults, so nothing can persist across a reopen.
 */
function renderSlotsState() {
  refs.timeSkeleton()?.classList.add('tw-hidden');
  refs.timeRetry()?.classList.add('tw-hidden');
  refs.timeField()?.classList.remove('tw-hidden');

  const select = refs.time();
  if (select) select.disabled = !slotsReady();
  updateSubmitState();
}

/**
 * The v4 response's per-store catering delivery times.
 *
 * EA-3475: v4 now answers CATERING delivery with a per-store list at
 * `delivery[].stores[].cateringDeliveryTimes`, clamped to that store's real
 * catering schedule — where the top-level `deliveryTimeSlots` is a generic list.
 * The store matching the shopper's selection wins; when no entry names it, the
 * first store carrying the field stands in (the example contract is
 * `delivery[0].stores[0].cateringDeliveryTimes`).
 *
 * Returns null — not [] — when NO store in the response carries the field at all,
 * so callers can tell "the API doesn't speak this shape yet" (fall back to
 * `deliveryTimeSlots`) apart from "the store has no catering times on this date"
 * (an empty array, which must NOT fall back to the generic list).
 *
 * @param {any} data the raw v4 response body
 * @param {string|number|undefined} storeId the shopper's selected store
 * @returns {string[]|null}
 */
function extractCateringDeliveryTimes(data, storeId) {
  const entries = Array.isArray(data?.delivery) ? data.delivery : [];
  let fallback = null;
  for (const entry of entries) {
    const stores = Array.isArray(entry?.stores) ? entry.stores : [];
    for (const store of stores) {
      const times = Array.isArray(store?.cateringDeliveryTimes) ? store.cateringDeliveryTimes : null;
      if (!times) continue;
      if (storeId != null && String(store.storeId) === String(storeId)) return times;
      if (!fallback) fallback = times;
    }
  }
  return fallback;
}

/**
 * Ask the availability endpoint for the bookable times, or read them from cache.
 *
 * The ONE implementation of this request, and the only place its payload, its
 * parsing and its cache live. Both hosts call it before they open the drawer —
 * the cart drawer by importing it, the catering cart through the
 * `window.requestFulfillmentAvailability` global (that module is deliberately
 * import-free) — and hand the resolved object straight back as
 * `openFulfillmentDetails({ availability })`.
 *
 * Cached per store + date + cart, so a host that asks twice for the same
 * checkout (a second click, a shopper who backed out and returned) pays once.
 * One response carries BOTH arrays, so pickup and delivery are answered together.
 *
 * @param {{productIds?: number[], force?: boolean}} [options] `force` bypasses the
 *   cache.
 * @returns {Promise<{pickup: string[], delivery: string[], cateringDelivery: string[]|null, schedule: any, at: number}|null>}
 *   null when there's no store or date to ask about. Throws on a transport error,
 *   a non-2xx, a timeout or an unreadable body.
 */
export async function requestAvailability(options) {
  const selection = getStoreSelection();

  // Nothing to ask about without a store and a date. Both callers already route
  // the shopper to the store selector in that state, so say "no answer" rather
  // than sending a request that can only fail.
  if (!selection.storeId || !selection.date) return null;

  const productIds = Array.isArray(options?.productIds) ? options.productIds : [];
  const cacheKey = `${selection.storeId}:${selection.date}:${productIds.join(',')}`;
  const cached = options?.force ? null : _availabilityCache.get(cacheKey);
  // Aged out on the same clock as a chosen time: slots the store has since stopped
  // offering shouldn't come back from cache an hour into a session.
  if (cached && Date.now() - cached.at < TIME_SELECTION_TTL_MS) return cached;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AVAILABILITY_TIMEOUT_MS);

  try {
    const response = await fetch(AVAILABILITY_API.product, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        productIds,
        date: selection.date,
        country: selection.country,
        zipCode: selection.zipCode,
        address1: '',
        address2: '',
        city: '',
        state: '',
        storeId: Number.isFinite(Number(selection.storeId)) ? Number(selection.storeId) : selection.storeId,
      }),
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    const resolved = {
      pickup: Array.isArray(data?.pickupTimeSlots) ? data.pickupTimeSlots : [],
      delivery: Array.isArray(data?.deliveryTimeSlots) ? data.deliveryTimeSlots : [],
      // Catering delivery renders THIS list, not `delivery` — see
      // extractCateringDeliveryTimes. null when the response doesn't carry it.
      cateringDelivery: extractCateringDeliveryTimes(data, selection.storeId),
      schedule: data?.storeSchedule || null,
      at: Date.now(),
    };
    _availabilityCache.set(cacheKey, resolved);
    return resolved;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Commit the host's availability response to state and build the dropdown.
 *
 * Tolerates nothing being passed. That shouldn't happen — a host that couldn't
 * get an answer refuses to open the drawer at all — so it's logged, and the
 * shopper gets the ordinary "no times" message (populateTimeSlots raises it) with
 * submit held disabled, rather than an empty dropdown that looks bookable.
 *
 * @param {{pickup: string[], delivery: string[], schedule: any}|null|undefined} resolved
 *   as returned by requestAvailability
 */
function applyAvailability(resolved) {
  if (!resolved) {
    console.error('[fulfillment-details] opened without availability data — no times to offer');
    _slots = { pickup: [], delivery: [], cateringDelivery: null };
    _storeSchedule = null;
    _slotsState = 'idle';
    renderSlotsState();
    // Only when a store IS chosen. Without one the opener has already said so, and
    // that message is the useful one — "no times for this date" would be answering a
    // question the shopper hasn't got to yet.
    if (hasStoreSelection()) {
      showError(noSlotsMessage());
      showChangeStore(true);
    }
    return;
  }

  _slots = {
    pickup: Array.isArray(resolved.pickup) ? resolved.pickup : [],
    delivery: Array.isArray(resolved.delivery) ? resolved.delivery : [],
    cateringDelivery: Array.isArray(resolved.cateringDelivery) ? resolved.cateringDelivery : null,
  };
  _storeSchedule = resolved.schedule || null;
  _slotsState = 'ready';
  renderSlotsState();
  populateTimeSlots();
}

/** "09:30:00" or "9:30 AM" → minutes since midnight, or null. */
function parseScheduleTime(value) {
  const str = String(value || '').trim();
  const iso = str.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (iso) return +iso[1] * 60 + +iso[2];
  return parseClockTime(str);
}

/**
 * The catering tomorrow rule — the only slot logic left on the frontend.
 *
 * Ordering today for tomorrow, how much of today is left decides how much prep
 * time the store has: with 3+ hours before today's close the first pickup slot is
 * an hour after opening, and inside that window it's four hours after.
 *
 * Applied as a FILTER on the API's list rather than a computed start time, which
 * matters twice over. `OpeningTime + offset` needn't land on the returned grid
 * (opening at 9:15 gives a 10:15 threshold against 09:30/10:00/10:30 slots — the
 * answer is 10:30, not an invented 10:15). And if the API ever starts applying
 * this rule itself, filtering to the same threshold becomes a no-op instead of
 * shifting the list a second time.
 *
 * Pickup only, catering only, tomorrow only — every other case renders the API
 * response untouched.
 *
 * @param {string[]} slots the API's pickup slots
 * @param {Date} now
 * @returns {string[]}
 */
function applyCateringTomorrowRule(slots, now) {
  if (_bookingRules !== BOOKING_RULES.catering || !isPickupSelected()) return slots;
  if (!_storeSchedule || slots.length === 0) return slots;
  if (daysFromToday(getStoreSelection().date, now) !== 1) return slots;

  const opening = parseScheduleTime(_storeSchedule.OpeningTime);
  const closing = parseScheduleTime(_storeSchedule.ClosingTime);
  if (opening == null || closing == null) return slots;

  // NOTE: `closing` comes from the response for the SELECTED date (tomorrow),
  // used here as today's closing time. Correct whenever today and tomorrow keep
  // the same hours; flagged for QA on stores that don't.
  const minutesLeftToday = closing - (now.getHours() * 60 + now.getMinutes());
  const offset =
    minutesLeftToday >= CATERING_TOMORROW.cutoff
      ? CATERING_TOMORROW.earlyOffset
      : CATERING_TOMORROW.lateOffset;

  const threshold = opening + offset;
  return slots.filter((slot) => {
    const minutes = parseClockTime(slot);
    // An unparseable slot is kept rather than dropped: the API is authoritative,
    // and silently hiding a time it offered is worse than showing one early.
    return minutes == null || minutes >= threshold;
  });
}

// ── Time slots ────────────────────────────────────────────────────────────────

/**
 * The catering tomorrow rule, all in minutes. See applyCateringTomorrowRule —
 * these shift the first bookable pickup slot past the store's OPENING time based
 * on how much of today is left to prep in.
 */
const CATERING_TOMORROW = {
  cutoff: 180, // "ordered late today" = less than 3h before today's close
  earlyOffset: 60, // 3h or more left today → opening + 1h
  lateOffset: 240, // less than 3h left today → opening + 4h
};

/** Weekday (0=Sun … 6=Sat) for a "YYYY-MM-DD" or "MM/DD/YYYY" date, or -1. */
function weekdayIndex(dateStr) {
  if (!dateStr) return -1;
  let y;
  let mo;
  let d;
  let m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    y = +m[1]; mo = +m[2]; d = +m[3];
  } else {
    m = String(dateStr).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return -1;
    mo = +m[1]; d = +m[2]; y = +m[3];
  }
  const dt = new Date(y, mo - 1, d);
  return isNaN(dt.getTime()) ? -1 : dt.getDay();
}

/** Parse "9:00 AM" → minutes since midnight, or null. */
function parseClockTime(str) {
  const m = String(str).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return null;
  let h = +m[1] % 12;
  if (/pm/i.test(m[3])) h += 12;
  return h * 60 + +m[2];
}

/** Whole days from `now`'s date to `dateStr` — 0 today, 1 tomorrow, 2+ beyond. */
function daysFromToday(dateStr, now) {
  const dow = weekdayIndex(dateStr);
  if (dow < 0) return null;
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const target = m
    ? new Date(+m[1], +m[2] - 1, +m[3])
    : new Date(String(dateStr).replace(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, '$3-$1-$2') + 'T00:00:00');
  if (isNaN(target.getTime())) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86400000);
}

/**
 * The message for a date the API returned no times for. Nothing is wrong here —
 * this date simply has nothing left, so the shopper needs to pick another rather
 * than retry.
 *
 * Exported (and exposed as `window.fulfillmentNoSlotsMessage`) because the hosts
 * are where an empty list is now caught, before the drawer opens: the shopper gets
 * the same sentence wherever it's raised, and it stays editable in one place.
 *
 * @returns {string}
 */
export function noSlotsMessage() {
  const offset = daysFromToday(getStoreSelection().date, new Date());
  if (offset == null) return t('noDateError', 'Choose a fulfillment date to see the available times.');
  if (offset < 0) return t('pastDateError', 'That date has already passed. Please choose a new date.');
  if (offset === 0) return t('dayOverError', 'There are no times left today. Please choose a later date.');
  return t('noTimesError', 'This store has no available times on the selected date. Please choose another date.');
}

/**
 * Rebuild the time dropdown from the last availability response, keeping the
 * placeholder option and the shopper's chosen slot when it's still on offer.
 *
 * Renders whichever array matches the saved fulfillment type — one response
 * carries both, so toggling pickup/delivery re-renders without re-requesting.
 * Nothing here generates slot values; the only transform is the catering tomorrow
 * filter, which drops leading slots and never invents one.
 */
function populateTimeSlots() {
  const select = refs.time();
  if (!select) return;

  const previous = select.value || '';
  // Catering delivery books against the store's catering schedule
  // (`cateringDeliveryTimes`), not the generic `deliveryTimeSlots` list. Only a
  // null (response predates the field) falls back — an empty catering list means
  // "no catering times on this date" and must render as such.
  const catering = _bookingRules === BOOKING_RULES.catering;
  const deliverySlots = catering && _slots.cateringDelivery !== null ? _slots.cateringDelivery : _slots.delivery;
  const fromApi = isPickupSelected() ? _slots.pickup : deliverySlots;
  const slots = applyCateringTomorrowRule(fromApi, new Date());

  let placeholder = select.querySelector('option[value=""]');
  if (!placeholder) {
    placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = t('pickupTimePlaceholder', 'Select Time');
  }
  select.innerHTML = '';
  select.appendChild(placeholder);
  for (const slot of slots) {
    const option = document.createElement('option');
    option.value = slot;
    option.textContent = slot;
    select.appendChild(option);
  }

  // Put the shopper's slot back. Assigning a value the list no longer has leaves
  // the select on the placeholder — which is a silent change to something they
  // already chose, so say so rather than let them wonder.
  if (previous) {
    select.value = previous;
    if (select.value !== previous) {
      showError(t('timeUnavailableError', 'That time is no longer available. Please choose another.'));
      updateSubmitState();
    }
  }

  // Only speak up once the request has landed — while it's in flight the skeleton
  // is the message, and a failed request already showed its own error.
  if (slots.length === 0 && slotsReady() && hasStoreSelection()) {
    showError(noSlotsMessage());
    // Offer the way out. Must follow showError, which clears the action by default.
    showChangeStore(true);
  }
}

// ── Fields ────────────────────────────────────────────────────────────────────

function updateInstructionCount() {
  const instruction = refs.instruction();
  const counter = refs.instructionCount();
  if (instruction && counter) counter.textContent = `${instruction.value.length}/${INSTRUCTION_MAX_LENGTH}`;
}

/** The values currently entered. */
function readValues() {
  const pickup = isPickupSelected();
  return {
    orderType: pickup ? 'Pickup' : 'Delivery',
    time: refs.time()?.value || '',
    firstName: refs.firstName()?.value?.trim() || '',
    lastName: refs.lastName()?.value?.trim() || '',
    phone: refs.phone()?.value?.trim() || '',
    // Delivery only — the field is hidden and cleared for pickup.
    email: pickup ? '' : refs.email()?.value?.trim() || '',
    // The one optional field.
    instruction: refs.instruction()?.value?.trim() || '',
  };
}

/**
 * The country that phone validation should use right now.
 *
 * Reads dynamically from the store selection (localStorage) so that switching
 * stores/countries in the header selector changes validation without a page
 * reload. Falls back to the config island's static Liquid-baked value, then to
 * 'US' — preserving existing NANP behaviour when nothing else is available.
 *
 * Reads the RAW stored country rather than getStoreSelection().country — that
 * accessor defaults to 'US' when nothing is stored, which would make the
 * config-island fallback unreachable and pin a localized storefront to US
 * validation before a store is ever selected.
 *
 * @returns {string} uppercased ISO 3166-1 alpha-2
 */
function getValidationCountry() {
  let stored = '';
  try {
    const raw = window.localStorage.getItem(STORE_SELECTION_KEY);
    if (raw) {
      const state = JSON.parse(raw);
      if (state && typeof state === 'object') stored = state.country || '';
    }
  } catch { stored = ''; }
  if (stored) return String(stored).toUpperCase();
  const fromIsland = t('country', '');
  if (fromIsland) return String(fromIsland).toUpperCase();
  return 'US';
}

/**
 * Whether `cc` is a North American Numbering Plan country (US or Canada).
 * NANP countries share the same 10-digit format and area-code rules.
 * @param {string} cc uppercased ISO country code
 * @returns {boolean}
 */
function isNanpCountry(cc) {
  return cc === 'US' || cc === 'CA';
}

/**
 * Reduce whatever was typed or pasted to the digits the phone field accepts.
 *
 * NANP countries (US, CA): non-digits dropped, a leading country code ('1')
 * removed, capped at 10. So "+1 (860) 987-6543" and "860.987.6543" both land
 * on "8609876543".
 *
 * Non-NANP countries: non-digits dropped, capped at 15 (E.164 maximum).
 *
 * @param {string} raw
 * @param {string} [country] ISO country code; defaults to getValidationCountry()
 * @returns {string}
 */
export function normalizePhone(raw, country) {
  const cc = country !== undefined ? String(country).toUpperCase() : getValidationCountry();
  let digits = String(raw ?? '').replace(/\D/g, '');
  if (isNanpCountry(cc)) {
    if (digits.length > 10 && digits.startsWith('1')) digits = digits.slice(1);
    return digits.slice(0, 10);
  }
  return digits.slice(0, 15);
}

/**
 * Validate a phone number against the rules for the resolved country.
 *
 * NANP countries (US, CA): 10 digits, area code and exchange code both starting
 * 2-9, and neither being an N11 service code (411, 611, 911 …). The two
 * countries share one plan, so only the area code distinguishes them — pinning a
 * number to one would mean carrying a list of Canadian area codes that turns
 * away a real customer every time the NANP issues one this file hasn't heard of.
 * The MESSAGE names the selected country (see invalidPhoneMessage); the rule
 * stays the same for both.
 *
 * Non-NANP countries: accepts 7–15 digits (E.164 range). Intentionally lenient
 * — maintaining per-country regex databases is impractical.
 *
 * @param {string} value
 * @param {string} [country] ISO country code; defaults to getValidationCountry()
 * @returns {boolean}
 */
export function isValidPhone(value, country) {
  const cc = country !== undefined ? String(country).toUpperCase() : getValidationCountry();
  const digits = normalizePhone(value, cc);
  if (isNanpCountry(cc)) {
    if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return false;
    const isServiceCode = (code) => code[1] === '1' && code[2] === '1';
    return !isServiceCode(digits.slice(0, 3)) && !isServiceCode(digits.slice(3, 6));
  }
  return digits.length >= 7 && digits.length <= 15;
}

/**
 * The "that's not a valid number" message, naming the country the storefront is
 * currently set to — telling a Canadian shopper their number should be "US or
 * Canada" doesn't say what's wrong with it.
 *
 * The country comes from `getValidationCountry()`, which reads dynamically from
 * the store selection (localStorage). This means the error message reflects the
 * country the shopper actually selected — not the page-render-time Liquid value
 * — and updates without a reload when the store/country changes.
 *
 * NANP countries (US, CA) get a country-specific 10-digit message; all others
 * get a generic message that doesn't mention digit counts or country names.
 *
 * @returns {string}
 */
function invalidPhoneMessage() {
  switch (getValidationCountry()) {
    case 'CA':
      return t('invalidPhoneErrorCa', 'Enter a valid 10-digit Canadian phone number.');
    case 'US':
      return t('invalidPhoneErrorUs', 'Enter a valid 10-digit US phone number.');
    default:
      return t('invalidPhoneError', 'Enter a valid phone number.');
  }
}

export function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? '').trim());
}

/**
 * Required: the time, the recipient's first name, last name and phone — plus the
 * recipient email for a delivery. The instruction is optional.
 */
function isComplete(values) {
  const v = values || readValues();
  if (!v.time || !v.firstName || !v.lastName) return false;
  if (!isValidPhone(v.phone)) return false;
  if (String(v.orderType).toLowerCase() !== 'pickup' && !isValidEmail(v.email)) return false;
  return true;
}

function updateSubmitState() {
  const btn = refs.submit();
  if (!btn) return;
  // Blocked unless the host handed over slots: with none there is no valid time to
  // submit, so Continue can't do anything useful.
  btn.disabled = _submitting || !slotsReady() || !isComplete();
}

function setSubmitting(submitting) {
  _submitting = submitting;
  const btn = refs.submit();
  if (btn) {
    btn.setAttribute('aria-busy', submitting ? 'true' : 'false');
    btn.classList.toggle('is-loading', submitting);
  }
  updateSubmitState();
}

/**
 * Show (or, with no message, hide) the footer error text. Reserved for problems
 * that aren't about one field — a failed save, or no store/slots available.
 * Field-level messages belong under their input; see showFieldError.
 */
function showError(message) {
  const el = refs.error();
  if (!el) return;
  el.textContent = message || '';
  el.classList.toggle('tw-hidden', !message);
  // The action belongs to the no-slots message alone, so it's hidden by default here
  // and re-shown explicitly by whoever raises that one. Without this, it would linger
  // beside a later, unrelated message.
  showChangeStore(false);
}

/**
 * Show or hide the "change store or date" action beside the footer error.
 *
 * Offered only when the drawer was opened with no times for the current store and
 * date — the one failure the shopper can act on from in here. The hosts stop that
 * case before it opens, so this is now a backstop rather than the usual path.
 *
 * @param {boolean} visible
 */
function showChangeStore(visible) {
  refs.changeStore()?.classList.toggle('tw-hidden', !visible);
}

/**
 * Show (or, with no message, clear) a validation message directly below a field,
 * and mark the input invalid so it picks up the red border.
 *
 * The target is an aria-live region, so it must stay rendered (never
 * display:none) for screen readers to announce a message injected into it —
 * emptiness alone hides it visually (see the __field-error:empty rule).
 *
 * @param {HTMLElement|null} input
 * @param {HTMLElement|null} target the field's own error element
 * @param {string} message
 */
function showFieldError(input, target, message) {
  if (target) {
    target.textContent = message || '';
  }
  if (input) {
    if (message) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  }
}

/** Clear every field-level message (e.g. on open, reset, or a fresh submit). */
function clearFieldErrors() {
  showFieldError(refs.time(), refs.timeError(), '');
  showFieldError(refs.phone(), refs.phoneError(), '');
  showFieldError(refs.email(), refs.emailError(), '');
}

// ── Completion ────────────────────────────────────────────────────────────────

/**
 * Write the collected details to the online-store cart. Attributes with an empty
 * value clear whatever was there before (see buildFulfillmentAttributeMap).
 * @param {Record<string, string>} attributes
 * @returns {Promise<void>} rejects on a non-OK response, a network failure or a timeout
 */
async function writeCartAttributes(attributes) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CART_UPDATE_TIMEOUT_MS);
  try {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`/cart/update.js responded ${response.status}`);
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Submit handler: validate, write the cart attributes, then redirect.
 *
 * A host can intercept by cancelling `fulfillment-details:saved` — used when the
 * order doesn't go through the online-store cart (e.g. the catering flow's
 * Storefront `cartCreate`). Cancelling skips the cart write AND the redirect;
 * the host owns both from there.
 */
async function submitDetails() {
  if (_submitting) return;

  // A time chosen more than half an hour ago is no longer trustworthy — the
  // bookable window has moved on since. Drop it, rebuild the list against the
  // current clock, and ask for it again rather than sending a slot the store may
  // have stopped offering.
  if (isTimeSelectionStale()) {
    const select = refs.time();
    if (select) select.value = '';
    _timeSelectedAt = 0;
    populateTimeSlots();
    showFieldError(select, refs.timeError(), t('timeExpiredError', 'Your time selection has expired. Please choose a time again.'));
    updateSubmitState();
    select?.focus();
    return;
  }

  const values = readValues();
  if (!isComplete(values)) {
    updateSubmitState();
    return;
  }

  const attributes = buildFulfillmentAttributeMap(values);
  const selection = getStoreSelection();

  clearFieldErrors();
  showError('');
  setSubmitting(true);

  const savedEvent = new CustomEvent('fulfillment-details:saved', {
    cancelable: true,
    detail: { values, attributes, isPickup: isPickupSelected(), storeSelection: selection },
  });
  const hostHandled = !document.dispatchEvent(savedEvent);

  if (hostHandled) {
    // The host is driving from here (its own cart + redirect). Close and hand off.
    setSubmitting(false);
    closeFulfillmentDetails({ saving: true });
    return;
  }

  try {
    await writeCartAttributes(attributes);
  } catch (err) {
    console.error('[fulfillment-details] could not save details to the cart:', err);
    showError(t('saveError', "We couldn't save your details. Please try again."));
    setSubmitting(false);
    return;
  }

  // Checkout opens on the pickup point only if the cart's delivery preference names
  // this store. Bounded wait: checkout's own validation covers a sync that can't finish.
  // Only while the pickup-point theme setting is on.
  if (window.eaPickupPointPreferenceEnabled === true) await import('@theme/ea-pickup-point-preference')
    .then((module) => module.ensurePickupPointPreference())
    .catch(() => {});

  closeFulfillmentDetails({ saving: true });
  // Keep the button busy through the navigation — releasing it would flash an
  // interactive drawer while the browser is already leaving.
  window.location.href = _redirectTo;
}

// ── Event binding ─────────────────────────────────────────────────────────────

function bindEvents() {
  // Open — delegated, so any trigger anywhere on the page works, including
  // markup rendered after this module ran.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest?.('[data-open-fulfillment-details]');
    if (!trigger) return;
    e.preventDefault();
    openFulfillmentDetails({ redirectTo: trigger.dataset.fulfillmentRedirect });
  });

  document.addEventListener('fulfillment-details:open', (e) => {
    openFulfillmentDetails(e.detail || undefined);
  });

  // Close
  document.addEventListener('click', (e) => {
    if (e.target.closest?.('[data-fulfillment-back]')) {
      goBack();
      return;
    }
    if (e.target.closest?.('[data-fulfillment-close]')) {
      closeFulfillmentDetails();
      return;
    }
    if (e.target.matches?.('[data-fulfillment-overlay]')) closeFulfillmentDetails();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (_root && !_root.classList.contains('tw-translate-x-full')) closeFulfillmentDetails();
  });

  // Trap Tab within the drawer while it's open. Bound on the DOCUMENT (capture), not
  // the root, so it contains focus wherever it currently sits — including when focus
  // has escaped to the background page (see trapTabKey). trapTabKey no-ops while the
  // drawer is closed, so this is inert the rest of the time.
  document.addEventListener('keydown', trapTabKey, true);

  // Restored from the back/forward cache (e.g. the shopper tapped Back from the
  // checkout): scripts don't re-run but JS state survives, so the busy flag left
  // set for the redirect would keep the submit button disabled for good.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) setSubmitting(false);
  });

  // A store/date/fulfillment change invalidates the copy and the generated slots.
  document.addEventListener('store-availability:change', (e) => {
    configureForSelection();
    populateTimeSlots();
    showError('');

    // The store selection was cleared. Details entered against it no longer
    // apply, so drop them from the cart too — otherwise a time and recipient
    // from the abandoned store would still ride along to checkout.
    if (!e.detail) {
      writeCartAttributes(buildFulfillmentAttributeMap({})).catch(() => {
        // Best-effort; the shopper has to pick a store again before checkout.
      });
    }

    // An open request that was waiting on a store selection continues now — with
    // the options it was originally made with, not just its redirect target.
    const pending = _openAfterStoreSelectionAt && Date.now() - _openAfterStoreSelectionAt < PENDING_OPEN_TTL_MS;
    const pendingOptions = _pendingOpenOptions;
    _openAfterStoreSelectionAt = 0;
    // Cleared whether or not it's replayed below, so an expired request can't be
    // picked up by a later store change.
    _pendingOpenOptions = null;
    if (pending && hasStoreSelection()) {
      // Replay the original open with the options it was made with — including the
      // host-supplied `availability`. The drawer renders what it's handed and makes
      // no request of its own, so nothing else needs to run here.
      openFulfillmentDetails(pendingOptions || { redirectTo: _redirectTo });
      return;
    }

    // The slots on screen belong to a store + date that just changed, and this
    // module can't ask for new ones — the host resolves them as part of deciding
    // whether to open. So close, and let the shopper come back through the button
    // that validates: for the cart drawer `onBack` puts them straight back on the
    // cart, and catering closes to the collection page it opened from.
    if (isDrawerOpen()) {
      if (_onBack) goBack();
      else closeFulfillmentDetails();
      return;
    }

    updateSubmitState();
  });

  // Fields
  refs.time()?.addEventListener('change', (e) => {
    // Stamp only a real choice: reopening the dropdown rebuilds the list (which
    // preserves the current value) and must not renew the 30-minute clock.
    _timeSelectedAt = e.target.value ? Date.now() : 0;
    showFieldError(e.target, refs.timeError(), '');
    showError('');
    updateSubmitState();
  });

  // No retry listener: this module makes no request, so there is nothing here to
  // retry. A failed availability request stops the HOST before the drawer opens,
  // and the shopper retries by clicking its button again. The button's markup is
  // left in the snippet but never shown — see renderSlotsState.

  // No times for this store and date — send the shopper back to the selector to change
  // either. The drawer is left open behind it: the store selector is a native <dialog>
  // opened with showModal(), so it sits in the top layer and renders above this panel
  // regardless of z-index. Choosing a new store or date fires
  // `store-availability:change`, which now CLOSES this drawer (see that listener): the
  // new selection needs a fresh availability answer, and only the host can get one.
  // The shopper lands back where they started and re-clicks its button.
  refs.changeStore()?.addEventListener('click', () => {
    if (typeof window.openStoreAvailability === 'function') {
      window.openStoreAvailability();
      return;
    }
    // No selector on this page, so the offer was empty — say so rather than leaving a
    // button that does nothing.
    console.error('[fulfillment-details] store selector unavailable — cannot change store');
    showChangeStore(false);
  });

  refs.instruction()?.addEventListener('input', updateInstructionCount);

  for (const field of [refs.firstName(), refs.lastName()]) {
    field?.addEventListener('input', () => {
      showError('');
      updateSubmitState();
    });
  }

  // Phone accepts digits only — anything else is stripped as it's typed, so the
  // field can never hold a character the validation would reject.
  refs.phone()?.addEventListener('input', (e) => {
    const normalized = normalizePhone(e.target.value);
    if (e.target.value !== normalized) e.target.value = normalized;
    showFieldError(e.target, refs.phoneError(), '');
    showError('');
    updateSubmitState();
  });

  refs.email()?.addEventListener('input', (e) => {
    showFieldError(e.target, refs.emailError(), '');
    showError('');
    updateSubmitState();
  });

  // The submit button alone can't explain a phone/email that's filled but
  // malformed, so each field says so under itself once the shopper leaves it.
  refs.phone()?.addEventListener('blur', (e) => {
    const invalid = e.target.value.trim() && !isValidPhone(e.target.value);
    showFieldError(e.target, refs.phoneError(), invalid ? invalidPhoneMessage() : '');
  });

  refs.email()?.addEventListener('blur', (e) => {
    const invalid = e.target.value.trim() && !isValidEmail(e.target.value);
    showFieldError(e.target, refs.emailError(), invalid ? t('invalidEmailError', 'Enter a valid email address.') : '');
  });

  refs.submit()?.addEventListener('click', submitDetails);
}

// ── Init ──────────────────────────────────────────────────────────────────────

function init() {
  _root = document.querySelector('[data-fulfillment-drawer]');
  _overlay = document.querySelector('[data-fulfillment-overlay]');
  // Not mounted on this page — the delegated openers below stay harmless no-ops.
  if (!_root || !_overlay) return;

  if (document.querySelectorAll('[data-fulfillment-drawer]').length > 1) {
    console.warn('[fulfillment-details] more than one drawer is mounted; only the first is wired up.');
  }

  // Transitional: an earlier build of this drawer cached the collected details
  // under this key. It has no writer any more, so clear anything left behind in a
  // browser that ran that build. Safe to delete once testers' storage is clean.
  try {
    window.localStorage.removeItem('ea-fulfillment-details');
  } catch {
    // localStorage unavailable — nothing to clean up.
  }

  loadCopy();
  bindEvents();
  configureForSelection();
  // No populateTimeSlots() here on purpose — the slots depend on the clock, so
  // they're built when the drawer or the dropdown opens, not on page load.
  updateInstructionCount();
  updateSubmitState();
}

// Globals so a script can drive the drawer without importing this module — which
// assets/catering-cart.js deliberately doesn't. The opener has always been here;
// the other two are what a host needs now that IT resolves the slots: the request
// itself, and the sentence to show when the answer comes back empty. The cart
// drawer imports the same three functions directly.
if (typeof window !== 'undefined') {
  window.openFulfillmentDetails = openFulfillmentDetails;
  window.requestFulfillmentAvailability = requestAvailability;
  window.fulfillmentNoSlotsMessage = noSlotsMessage;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
